import _ from 'lodash';
import logger from './logging/logger';

export const isFixedOrSticky = (targetNode: HTMLElement) => {
    let node: HTMLElement | Element | null = targetNode;
    do {
        const { position } = getComputedStyle(node);
        if (position === 'sticky') {
            return 'sticky';
        } else if (position === 'fixed') {
            return 'fixed';
        }
    } while ('offsetParent' in node && (node = node.offsetParent));

    return false;
};

const TRANSPARENT_COLOR = ['transparent', 'rgba(0, 0, 0, 0)', ''];

export const getParentBackground = (targetNode: HTMLElement) => {
    let node: HTMLElement | null = targetNode;
    do {
        const bgColor = getComputedStyle(node).backgroundColor;
        if (!TRANSPARENT_COLOR.includes(bgColor)) {
            return bgColor;
        }
    } while ((node = node.parentElement));
    return undefined;
};

export const getParents = (targetNode: Element, includeCurrent?: boolean) => {
    const parents = includeCurrent ? [targetNode] : [];
    let node: Element | null = targetNode;
    while ((node = node?.parentElement)) {
        parents.push(node);
    }
    return parents;
};

export const getParent = (targetNode: Element, selectors: string | string[]) => {
    let node: Element | null = targetNode;
    selectors = _.castArray(selectors);
    do {
        // eslint-disable-next-line no-loop-func
        if (selectors.some((selector) => node?.matches(selector))) {
            return node;
        }
    } while ((node = node.parentElement));

    return null;
};

export const hasParent = (targetNode: Element, parentNode: HTMLElement) => {
    let node: Element | null = targetNode;
    do {
        if (node === parentNode) {
            return true;
        }
    } while ((node = node.parentElement));
    return false;
};

export const isNodeWrappedInLink = (node: HTMLElement) => {
    let el: HTMLElement | null = node;
    do {
        if (el.tagName.toLowerCase() === 'a') {
            const href = el.getAttribute('href') || '';
            if (href && href !== '#') {
                return true;
            }
        }
    } while ((el = el.parentElement)); // eslint-disable-line

    return false;
};

export const getOffset = (targetNode: HTMLElement) => {
    let top = 0;
    let left = 0;
    let node: HTMLElement | Element | null = targetNode;

    do {
        if ('offsetTop' in node) {
            top += node.offsetTop || 0;
            left += node.offsetLeft || 0;
        }
        node = 'offsetParent' in node ? node.offsetParent : null;
    } while (node);

    return {
        top,
        left
    };
};

export const placeCaretInEnd = (node: Element | Text) => {
    const range = document.createRange();
    if (node.lastChild) {
        range.selectNodeContents(node.lastChild);
    }
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
};

export const getCenterScrollPositionForNode = (node: HTMLElement) => {
    const maxScroll = document.documentElement.scrollHeight - innerHeight;
    let top = getOffset(node).top - window.innerHeight / 2 + node.offsetHeight / 2; // iframe scrollIntoView not working :(
    if (top < 0) {
        top = 0;
    } else if (top > maxScroll) {
        top = maxScroll;
    }

    return top;
};

export const scrollNodeIntoView = (node: HTMLElement, isCenter = true) => {
    if (!node || !node.scrollIntoView) {
        return false;
    }

    if (window.top === window) {
        node.scrollIntoView({ behavior: 'smooth', block: isCenter ? 'center' : 'nearest', inline: 'nearest' });
        return true;
    }

    if (isFixedOrSticky(node)) {
        return false; // dont scroll if fixed
    }

    if (!isCenter && isInViewport(node)) {
        return false;
    }

    const maxScroll = document.documentElement.scrollHeight - innerHeight;
    let top = getOffset(node).top - window.innerHeight / 2 + node.offsetHeight / 2; // iframe scrollIntoView not working :(
    if (top < 0) {
        top = 0;
    } else if (top > maxScroll) {
        top = maxScroll;
    }
    const willScroll = document.documentElement.scrollTop !== top;
    window.scrollTo({
        behavior: 'smooth',
        left: 0,
        top
    });

    return willScroll;
};

export const isAccessibleNode = (node: HTMLElement) => {
    if (!node) return false;
    if (!(node instanceof Element)) return false;
    if (!node.scrollIntoView) return false;
    if (!node.parentNode) return false;
    return true;
};

export const isVisibleNode = (node: HTMLElement) => {
    if (!isAccessibleNode(node)) {
        return false;
    }

    if (node.tagName.toLowerCase() === 'body') {
        return true;
    }

    const style = getComputedStyle(node);
    const boundingClientRect = node.getBoundingClientRect();
    const nodeWidth = boundingClientRect.width;
    const nodeHeight = boundingClientRect.height;

    if (style.display === 'none') return false;
    if (style.visibility !== 'visible') return false;
    // @todo make sure it works with px, rem, em
    if (nodeWidth <= 1) return false;
    if (nodeHeight <= 1) return false;
    if (node.offsetWidth + node.offsetHeight + nodeHeight + nodeWidth === 0) {
        return false;
    }

    const parents = getParents(node);
    let isVisible = true;
    _.forEach(parents, (parent, index) => {
        if (parent.tagName === 'DETAILS') {
            const prevNode = parents[index - 1] || node;
            if (prevNode.tagName === 'SUMMARY') {
                return false;
            }
            isVisible = (parent as HTMLDetailsElement).open;
            return false;
        }
        return undefined;
    });

    return isVisible;
};

export const isVisibleNodeDelayed = (node: HTMLElement) => {
    if (!isAccessibleNode(node)) {
        return Promise.resolve(false);
    }

    if (isVisibleNode(node)) {
        return Promise.resolve(true);
    }
    return new Promise((resolve) => {
        let retryCounter = 0;
        let isVisible = false;
        let checkVisibilityForAnimatedNodeRAF: number | null = null;

        const checkVisibilityForAnimatedNode = () => {
            checkVisibilityForAnimatedNodeRAF = requestAnimationFrame(() => {
                if (isVisible || retryCounter === 100) {
                    if (checkVisibilityForAnimatedNodeRAF) {
                        cancelAnimationFrame(checkVisibilityForAnimatedNodeRAF);
                        checkVisibilityForAnimatedNodeRAF = null;
                    }

                    resolve(isVisible);
                    return;
                }

                retryCounter += 1;
                isVisible = isVisibleNode(node);

                checkVisibilityForAnimatedNode();
            });
        };

        checkVisibilityForAnimatedNode();
    });
};

export const getTotalZIndex = (targetNode: HTMLElement) => {
    const indexes = [];
    let node: HTMLElement | Element | null = targetNode;

    do {
        const index = parseFloat(getComputedStyle(node).zIndex);
        if (!Number.isNaN(index)) {
            indexes.push(index);
        }
    } while ('offsetParent' in node && (node = node.offsetParent));

    return indexes.length ? Math.max(...indexes) : -Infinity;
};

export const isDomNodeOverlapped = (origEl: HTMLElement, overlapEl: HTMLElement) => {
    // if origEl covers overlapEl entirely and has zIndex more than overlapEl - return true;
    const origRect = origEl.getBoundingClientRect();
    const overlapRect = overlapEl.getBoundingClientRect();
    const overlap = !(
        origRect.right < overlapRect.left ||
        origRect.left > overlapRect.right ||
        origRect.bottom < overlapRect.top ||
        origRect.top > overlapRect.bottom
    );

    if (!overlap) {
        return false;
    }

    const origIndex = getTotalZIndex(origEl);
    const overlapIndex = getTotalZIndex(overlapEl);

    const hasBiggerIndex = origIndex > overlapIndex;

    if (hasBiggerIndex) {
        return true;
    }

    const origCssDecl = getComputedStyle(origEl);
    const hasBgContents = !['none', ''].includes(origCssDecl.backgroundImage) || !TRANSPARENT_COLOR.includes(origCssDecl.backgroundColor);

    return hasBgContents;
};

let offsetBase: HTMLElement | undefined;
export const getNodeViewportPosition = (el: HTMLElement) => {
    let found;
    let fixed = false;
    let sticky = false;
    let left = 0;
    let top = 0;
    let width = 0;
    let height = 0;
    if (!offsetBase && document.body) {
        offsetBase = document.createElement('div');
        offsetBase.style.cssText = 'position:absolute;left:0;top:0';
        offsetBase.classList.add('sb-widget-overlay-offset-base');
        document.body.appendChild(offsetBase);
    }
    if (el && el.ownerDocument === document && 'getBoundingClientRect' in el && offsetBase) {
        const boundingRect = el.getBoundingClientRect();
        const baseRect = offsetBase.getBoundingClientRect();
        found = true;
        const positionType = isFixedOrSticky(el);
        fixed = positionType === 'fixed';
        sticky = positionType === 'sticky';

        left = boundingRect.left;
        top = boundingRect.top;
        width = boundingRect.width;
        height = boundingRect.height;

        if (!fixed) {
            left -= baseRect.left;
            top -= baseRect.top;
            width -= baseRect.width;
            height -= baseRect.height;
        }
    }
    return {
        fixed,
        sticky,
        found,
        left,
        top,
        width,
        height,
        bottom: top + height,
        right: left + width
    };
};

export const isInViewport = (element: HTMLElement) => {
    const box = element.getBoundingClientRect();
    if (box.top > window.innerHeight || box.bottom < 0 || box.left > window.innerWidth || box.right < 0) {
        return false;
    }
    return true;
};

export const isInBounds = (bounds: DOMRect, clientX: number, clientY: number) =>
    clientX >= bounds.left && clientX <= bounds.right && clientY >= bounds.top && clientY <= bounds.bottom;

export const isEqualBounds = (a: DOMRect, b: DOMRect) =>
    a.bottom === b.bottom &&
    a.height === b.height &&
    a.left === b.left &&
    a.right === b.right &&
    a.top === b.top &&
    a.width === b.width &&
    a.x === b.x &&
    a.y === b.y;

export class DOMRectViewport extends DOMRect {
    constructor(rect: DOMRect) {
        super(rect.x + window.scrollX, rect.y + window.scrollY, rect.width, rect.height);
        Object.defineProperties(this, {
            left: { value: rect.left + window.scrollX },
            top: { value: rect.top + window.scrollY },
            right: { value: rect.right + window.scrollX },
            bottom: { value: rect.bottom + window.scrollY }
        });
    }
}

export const getScrollParent = (element: HTMLElement, includeHidden?: boolean) => {
    if (!element) {
        return document.documentElement;
    }

    let style = getComputedStyle(element);
    const excludeStaticParent = style.position === 'absolute';
    const overflowRegex = includeHidden ? /(auto|scroll|hidden)/ : /(auto|scroll)/;

    if (style.position === 'fixed') {
        return document.documentElement;
    }
    for (let parent: HTMLElement | Element | null = element; (parent = parent.parentElement); ) {
        style = getComputedStyle(parent);
        if (excludeStaticParent && style.position === 'static') {
            continue;
        }
        if (overflowRegex.test(style.overflow + style.overflowY + style.overflowX)) {
            return parent;
        }
    }

    return document.documentElement;
};

export const findNodesCommonAncestor = (nodeA: HTMLElement, nodeB: HTMLElement) => {
    const range = new Range();
    range.setStart(nodeA, 0);
    range.setEnd(nodeB, 0);
    if (range.collapsed) {
        range.setStart(nodeB, 0);
        range.setEnd(nodeA, 0);
    }
    return range.commonAncestorContainer;
};

const unitToNumber = ({ value, fontSize }: { value: string; fontSize: string }): number | undefined => {
    if (value.endsWith('px')) {
        return parseInt(value.replace('px', ''), 10);
    } else if (value.endsWith('em') && fontSize.endsWith('px')) {
        const valueNumber = parseInt(value.replace('em', ''), 10);
        const fontSizeNumber = parseInt(fontSize.replace('px', ''), 10);
        return Math.floor(fontSizeNumber * valueNumber);
    } else if (value.endsWith('%')) {
        const valueNumber = parseInt(value.replace('%', ''), 10);
        const fontSizeNumber = parseInt(fontSize.replace('px', ''), 10);
        return Math.floor((valueNumber * fontSizeNumber) / 100);
    }
    logger.error('Unsupported unit transformation', { value, fontSize });
    return undefined;
};

export const getBorderRadius = (node: HTMLElement, { width, height }: { width: number; height: number }): number | { x: number; y: number } | undefined => {
    const style = getComputedStyle(node);
    if (style.borderRadius.endsWith('%')) {
        const radius = parseInt(style.borderRadius.replace('%', ''), 10);
        if (radius < 50) {
            return 0;
        }

        return {
            x: Math.ceil((width * radius) / 100),
            y: Math.ceil((height * radius) / 100)
        };
    }
    return unitToNumber({ value: style.borderRadius, fontSize: style.fontSize });
};

export const getPopupCenter = ({ width, height }: { width: number; height: number }) => {
    const dualScreenLeft = window.screenLeft === undefined ? window.screenX : window.screenLeft;
    const dualScreenTop = window.screenTop === undefined ? window.screenY : window.screenTop;

    const clientWidth = document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width;
    const clientHeight = document.documentElement.clientHeight ? document.documentElement.clientHeight : screen.height;
    const availWidth = window.innerWidth ? window.innerWidth : clientWidth;
    const availHeight = window.innerHeight ? window.innerHeight : clientHeight;

    const systemZoom = availWidth / window.screen.availWidth;
    const left = (availWidth - width) / 2 / systemZoom + dualScreenLeft;
    const top = (availHeight - height) / 2 / systemZoom + dualScreenTop;

    return {
        top,
        left,
        systemZoom,
        width: width / systemZoom,
        height: height / systemZoom
    };
};
